import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { MermaidDiagram } from "@/components/mermaid-diagram";

interface MarkdownReaderProps {
  filePath: string;
}

export function MarkdownReader({ filePath }: MarkdownReaderProps) {
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMarkdown = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(`Failed to load document: ${response.statusText}`);
        }
        const text = await response.text();
        setContent(text);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load document");
      } finally {
        setLoading(false);
      }
    };

    fetchMarkdown();
  }, [filePath]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-6">
          <p className="text-destructive font-semibold">Error Loading Document</p>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="prose prose-slate dark:prose-invert max-w-none
      prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-foreground
      prose-h1:text-3xl prose-h1:mb-4 prose-h1:mt-8 prose-h1:text-foreground
      prose-h2:text-2xl prose-h2:mb-3 prose-h2:mt-6 prose-h2:text-foreground
      prose-h3:text-xl prose-h3:mb-2 prose-h3:mt-4 prose-h3:text-foreground
      prose-h4:text-lg prose-h4:mb-2 prose-h4:mt-3 prose-h4:text-foreground
      prose-p:text-base prose-p:leading-7 prose-p:text-foreground
      prose-a:text-primary prose-a:no-underline hover:prose-a:underline
      prose-strong:font-semibold prose-strong:text-foreground
      prose-code:text-sm prose-code:bg-muted prose-code:text-foreground prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
      prose-pre:bg-muted prose-pre:border prose-pre:text-foreground
      prose-blockquote:border-l-primary prose-blockquote:bg-muted/50 prose-blockquote:py-1 prose-blockquote:text-foreground
      prose-ul:list-disc prose-ul:text-foreground
      prose-ol:list-decimal prose-ol:text-foreground
      prose-li:text-base prose-li:leading-7 prose-li:text-foreground
      prose-table:border prose-table:border-collapse
      prose-th:border prose-th:p-3 prose-th:bg-muted prose-th:font-semibold prose-th:text-foreground
      prose-td:border prose-td:p-3 prose-td:text-foreground
      prose-tr:border-b
      prose-img:rounded-lg prose-img:shadow-md
      [&_em]:text-foreground [&_em]:not-italic [&_em]:font-normal
    ">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          code(props) {
            const { children, className, ...rest } = props;
            const match = /language-(\w+)/.exec(className || "");
            const language = match ? match[1] : "";
            const codeContent = String(children).replace(/\n$/, "");

            // Render Mermaid diagrams using the MermaidDiagram component
            if (language === "mermaid") {
              return (
                <div className="not-prose my-6">
                  <MermaidDiagram chart={codeContent} />
                </div>
              );
            }

            // Regular code blocks and inline code
            return (
              <code className={className} {...rest}>
                {children}
              </code>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
